import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  normalizeAnalysisBreakdown,
  getCorrectedVersion,
  getFeedbackSummary,
  getFeedbackText,
  getStrengths,
  getImprovements,
  getCriteriaEntries,
  getCriterionDisplayFeedback,
  type FeedbackKey,
} from '@/lib/analysisBreakdown';
import type { ModuleAnswer, ModuleResult } from '@/types/simulation-results';
import type { UserSubmission } from './types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ModulePrintViewProps {
  readonly submission: UserSubmission;
  readonly modules: string[];
  readonly answersMap: Record<string, ModuleAnswer[]>;
  readonly sessionModules: ModuleResult[];
  readonly onPrintDone: () => void;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  page: {
    fontFamily: 'Inter, system-ui, sans-serif',
    maxWidth: '780px',
    margin: '0 auto',
    padding: '28px 24px 16px',
    color: '#111827',
    fontSize: '13px',
    lineHeight: '1.5',
  } as React.CSSProperties,
  // Header
  docHeader: {
    borderBottom: '2.5px solid #111827',
    paddingBottom: '14px',
    marginBottom: '22px',
  } as React.CSSProperties,
  docTitle: {
    fontSize: '20px',
    fontWeight: 800,
    margin: '0 0 6px',
    letterSpacing: '-0.01em',
  } as React.CSSProperties,
  docMeta: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '14px',
    fontSize: '11px',
    color: '#6b7280',
  } as React.CSSProperties,
  docMetaItem: {
    display: 'flex',
    gap: '4px',
  } as React.CSSProperties,
  docMetaLabel: { fontWeight: 600, color: '#374151' },
  // Module header
  moduleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: '#111827',
    color: '#fff',
    borderRadius: '6px 6px 0 0',
    marginBottom: '0',
  } as React.CSSProperties,
  moduleTitle: { fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em' },
  moduleScore: { fontSize: '12px', fontWeight: 600 },
  // Section header
  sectionHeader: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.12em',
    color: '#6b7280',
    padding: '8px 0 4px',
    borderBottom: '1px solid #e5e7eb',
    marginBottom: '8px',
  } as React.CSSProperties,
  // Question card
  questionCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    marginBottom: '10px',
    overflow: 'hidden',
    pageBreakInside: 'avoid' as const,
    breakInside: 'avoid' as const,
  } as React.CSSProperties,
  questionCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '10px 12px 8px',
    borderBottom: '1px solid #f3f4f6',
    gap: '8px',
  } as React.CSSProperties,
  questionCardBody: {
    padding: '10px 12px',
  } as React.CSSProperties,
  // Badge row
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '4px',
    marginBottom: '6px',
  } as React.CSSProperties,
  badge: (color: 'gray' | 'green' | 'red' | 'amber' | 'blue' | 'dark') => {
    const map: Record<string, React.CSSProperties> = {
      dark: { background: '#111827', color: '#fff', borderColor: '#111827' },
      gray: { background: '#f3f4f6', color: '#374151', borderColor: '#d1d5db' },
      green: { background: '#dcfce7', color: '#15803d', borderColor: '#86efac' },
      red: { background: '#fee2e2', color: '#b91c1c', borderColor: '#fca5a5' },
      amber: { background: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' },
      blue: { background: '#dbeafe', color: '#1d4ed8', borderColor: '#93c5fd' },
    };
    return {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '1px 6px',
      borderRadius: '4px',
      fontSize: '9px',
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.06em',
      border: '1px solid',
      ...map[color],
    } as React.CSSProperties;
  },
  questionText: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#111827',
    lineHeight: '1.4',
    margin: '0',
  } as React.CSSProperties,
  // Answer grid
  answerGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '8px',
  } as React.CSSProperties,
  answerGridThree: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '10px',
    marginBottom: '8px',
  } as React.CSSProperties,
  fieldLabel: (color: 'green' | 'blue' | 'amber' | 'gray') => {
    const map: Record<string, string> = {
      green: '#15803d',
      blue: '#1d4ed8',
      amber: '#b45309',
      gray: '#6b7280',
    };
    return {
      display: 'block',
      fontSize: '9px',
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.1em',
      color: map[color],
      marginBottom: '4px',
    } as React.CSSProperties;
  },
  answerBox: (color: 'green' | 'blue' | 'amber' | 'gray') => {
    const map: Record<string, React.CSSProperties> = {
      green: { background: '#f0fdf4', border: '1px solid #86efac', color: '#14532d' },
      blue: { background: '#eff6ff', border: '1px solid #93c5fd', color: '#1e3a5f' },
      amber: { background: '#fffbeb', border: '1px solid #fcd34d', color: '#78350f' },
      gray: { background: '#f9fafb', border: '1px solid #e5e7eb', color: '#374151' },
    };
    return {
      padding: '8px 10px',
      borderRadius: '5px',
      fontSize: '12px',
      lineHeight: '1.5',
      whiteSpace: 'pre-wrap' as const,
      ...map[color],
    } as React.CSSProperties;
  },
  emptyAnswer: {
    padding: '8px 10px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '5px',
    fontSize: '12px',
    color: '#9ca3af',
    fontStyle: 'italic',
  } as React.CSSProperties,
  // Context block
  contextBlock: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    padding: '8px 10px',
    marginBottom: '8px',
    fontSize: '12px',
    color: '#374151',
  } as React.CSSProperties,
  contextLabel: {
    display: 'block',
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    color: '#6b7280',
    marginBottom: '3px',
  } as React.CSSProperties,
  // Analysis section
  analysisDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '10px 0 8px',
  } as React.CSSProperties,
  analysisDividerLine: { flex: 1, height: '1px', background: '#e5e7eb' } as React.CSSProperties,
  analysisDividerLabel: {
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.14em',
    color: '#9ca3af',
  } as React.CSSProperties,
  criteriaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px',
    marginBottom: '6px',
  } as React.CSSProperties,
  criterionBox: (theme: 'orange' | 'rose' | 'violet' | 'sky' | 'teal' | 'default') => {
    const map: Record<string, React.CSSProperties> = {
      orange: { background: '#fff7ed', border: '1px solid #fed7aa', color: '#7c2d12' },
      rose: { background: '#fff1f2', border: '1px solid #fecdd3', color: '#881337' },
      violet: { background: '#f5f3ff', border: '1px solid #ddd6fe', color: '#3b0764' },
      sky: { background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0c4a6e' },
      teal: { background: '#f0fdfa', border: '1px solid #99f6e4', color: '#134e4a' },
      default: { background: '#f9fafb', border: '1px solid #e5e7eb', color: '#374151' },
    };
    return {
      padding: '7px 9px',
      borderRadius: '5px',
      fontSize: '11px',
      lineHeight: '1.4',
      ...map[theme],
    } as React.CSSProperties;
  },
  criterionLabel: {
    display: 'block',
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    marginBottom: '3px',
    opacity: 0.7,
  } as React.CSSProperties,
  listBox: (color: 'green' | 'amber') => ({
    padding: '7px 9px',
    borderRadius: '5px',
    fontSize: '11px',
    lineHeight: '1.4',
    ...(color === 'green'
      ? { background: '#f0fdf4', border: '1px solid #86efac', color: '#14532d' }
      : { background: '#fffbeb', border: '1px solid #fcd34d', color: '#78350f' }),
  }) as React.CSSProperties,
  listLabel: {
    display: 'block',
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    marginBottom: '4px',
    opacity: 0.7,
  } as React.CSSProperties,
  moduleWrapper: { marginBottom: '28px' } as React.CSSProperties,
  moduleContentBorder: {
    border: '1px solid #e5e7eb',
    borderTop: 'none',
    borderRadius: '0 0 6px 6px',
    padding: '12px',
  } as React.CSSProperties,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CRITERION_THEMES: Record<string, 'orange' | 'rose' | 'violet' | 'sky' | 'teal' | 'default'> = {
  pronunciation: 'orange',
  grammar: 'rose',
  spelling: 'violet',
  structure: 'sky',
  fluency: 'teal',
  vocabulary: 'sky',
  task_fulfillment: 'teal',
  register: 'violet',
  style: 'orange',
  interaction: 'sky',
};

const MODULE_LABELS: Record<string, string> = {
  reading: 'Reading',
  listening: 'Listening',
  writing: 'Writing',
  speaking: 'Speaking',
};

const FEEDBACK_KEYS: FeedbackKey[] = [
  'task_fulfillment',
  'pronunciation',
  'grammar',
  'spelling',
  'fluency',
  'vocabulary',
  'structure',
  'register',
  'style',
  'interaction',
];

function formatQuestionType(raw?: string | null): string {
  if (!raw) return '';
  return raw
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function parseExpectedAnswers(raw?: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    if (typeof parsed === 'string') return parsed ? [parsed] : [];
  } catch {
    // not json
  }
  return [raw];
}

/** Converts "{wrong}[right]" diff notation to "wrong → right" */
function formatCorrectedVersion(raw: string): string {
  return raw.replace(/\{([^}]*)\}\[([^\]]*)\]/g, (_match, wrong, right) => {
    if (!wrong) return right;
    if (!right) return wrong;
    return `${wrong} → ${right}`;
  });
}

function groupBySection(answers: ModuleAnswer[]): Map<number, ModuleAnswer[]> {
  const groups = new Map<number, ModuleAnswer[]>();
  for (const a of answers) {
    const s = a.section ?? 1;
    const prev = groups.get(s) ?? [];
    groups.set(s, [...prev, a]);
  }
  return groups;
}

function sortAnswers(answers: ModuleAnswer[]): ModuleAnswer[] {
  return [...answers].sort(
    (a, b) =>
      (a.display_order ?? a.question_order ?? 0) -
      (b.display_order ?? b.question_order ?? 0),
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function QuestionCard({ answer, index }: { answer: ModuleAnswer; index: number }) {
  const breakdown = normalizeAnalysisBreakdown(answer);
  const correctedRaw = getCorrectedVersion(answer);
  const corrected = correctedRaw ? formatCorrectedVersion(correctedRaw) : '';
  const feedbackSummary = getFeedbackSummary(answer);
  const strengths = getStrengths(answer);
  const improvements = getImprovements(answer);
  const transcript = breakdown?.transcription?.text;
  const explanation = answer.explanation;

  const criteriaFeedback = FEEDBACK_KEYS
    .map((key) => ({ key, text: getFeedbackText(answer, key) }))
    .filter((c) => Boolean(c.text));

  // Also pick up any criteria entries not covered by named keys
  const criteriaEntries = getCriteriaEntries(answer)
    .filter(([key, entry]) => {
      const hasNamedText = criteriaFeedback.some((c) => c.key === key);
      if (hasNamedText) return false;
      const displayText = getCriterionDisplayFeedback(answer, key, entry);
      return Boolean(displayText);
    })
    .map(([key, entry]) => ({
      key,
      text: getCriterionDisplayFeedback(answer, key, entry),
    }));

  const allCriteria = [...criteriaFeedback, ...criteriaEntries];

  const isCorrect = answer.is_correct === true;
  const isUnanswered = !answer.answer_text && !answer.answer_audio_base64 && !answer.audio_url;
  const expectedAnswers = parseExpectedAnswers(answer.expected_answer);
  const showCorrected = Boolean(corrected);
  const gridCols = showCorrected ? S.answerGridThree : S.answerGrid;

  const hasAnalysis = Boolean(
    feedbackSummary || corrected || allCriteria.length > 0 ||
    strengths.length > 0 || improvements.length > 0 || explanation,
  );

  const statusColor = isUnanswered ? 'amber' : isCorrect ? 'green' : 'red';
  const statusLabel = isUnanswered ? 'Not Answered' : isCorrect ? 'Correct' : 'Incorrect';

  return (
    <div style={S.questionCard}>
      {/* Card header: badges + question text */}
      <div style={S.questionCardHeader}>
        <div style={{ flex: 1 }}>
          <div style={S.badgeRow}>
            <span style={S.badge('dark')}>Q{index + 1}</span>
            {answer.section != null && (
              <span style={S.badge('gray')}>Section {answer.section}</span>
            )}
            {answer.question_type && (
              <span style={S.badge('gray')}>{formatQuestionType(answer.question_type)}</span>
            )}
            {answer.points != null && (
              <span style={S.badge('gray')}>{answer.points} pts</span>
            )}
          </div>
          <p style={S.questionText}>{answer.question_text}</p>
        </div>
        <span style={S.badge(statusColor)}>{statusLabel}</span>
      </div>

      <div style={S.questionCardBody}>
        {/* Context block */}
        {(answer.context_content || answer.context_name) && (
          <div style={S.contextBlock}>
            <span style={S.contextLabel}>Context</span>
            {answer.context_name && (
              <span style={{ fontWeight: 600, marginRight: '6px', fontSize: '11px' }}>
                {answer.context_name}
              </span>
            )}
            {answer.context_content && (
              <span style={{ fontSize: '12px' }}>{answer.context_content}</span>
            )}
          </div>
        )}

        {/* Context transcript (listening) */}
        {answer.context_transcript && (
          <div style={{ ...S.contextBlock, marginBottom: '8px' }}>
            <span style={S.contextLabel}>Context Transcript</span>
            <span style={{ fontSize: '12px', fontStyle: 'italic' }}>{answer.context_transcript}</span>
          </div>
        )}

        {/* Answer columns */}
        <div style={gridCols}>
          {/* Expected answer */}
          <div>
            <span style={S.fieldLabel('green')}>Expected Answer</span>
            {expectedAnswers.length > 0 ? (
              <div style={S.answerBox('green')}>
                {expectedAnswers.map((ea, i) => (
                  <div key={i}>{expectedAnswers.length > 1 ? `• ${ea}` : ea}</div>
                ))}
              </div>
            ) : (
              <div style={S.emptyAnswer}>No specific expected answer.</div>
            )}
          </div>

          {/* User answer */}
          <div>
            <span style={S.fieldLabel('blue')}>Your Answer</span>
            {answer.answer_text || transcript ? (
              <div style={S.answerBox('blue')}>
                {answer.answer_text && (
                  <div>{answer.answer_text}</div>
                )}
                {transcript && transcript !== answer.answer_text && (
                  <>
                    {answer.answer_text && (
                      <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.6, margin: '6px 0 2px' }}>
                        Transcript
                      </div>
                    )}
                    <div style={{ fontStyle: answer.answer_text ? 'italic' : 'normal' }}>{transcript}</div>
                  </>
                )}
              </div>
            ) : (
              <div style={S.emptyAnswer}>
                {isUnanswered ? 'No answer provided.' : 'Audio recording (not printable).'}
              </div>
            )}
          </div>

          {/* Corrected version */}
          {showCorrected && (
            <div>
              <span style={S.fieldLabel('amber')}>Corrected Version</span>
              <div style={S.answerBox('amber')}>{corrected}</div>
            </div>
          )}
        </div>

        {/* Options (MCQ) */}
        {answer.options && answer.options.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <span style={S.contextLabel}>Options</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {answer.options.map((opt, i) => (
                <span
                  key={i}
                  style={{
                    ...S.badge(opt === answer.answer_text ? (isCorrect ? 'green' : 'red') : 'gray'),
                    fontWeight: opt === answer.answer_text ? 700 : 400,
                    textTransform: 'none',
                    fontSize: '11px',
                    letterSpacing: '0',
                  }}
                >
                  {opt}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Explanation */}
        {explanation && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ ...S.contextLabel, color: '#1d4ed8' }}>Explanation</span>
            <div style={{ ...S.contextBlock, background: '#eff6ff', border: '1px solid #93c5fd', color: '#1e3a5f', marginBottom: 0 }}>
              {explanation}
            </div>
          </div>
        )}

        {/* Feedback summary */}
        {feedbackSummary && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ ...S.contextLabel, color: '#6d28d9' }}>Feedback Summary</span>
            <div style={{ ...S.contextBlock, background: '#f5f3ff', border: '1px solid #ddd6fe', color: '#3b0764', marginBottom: 0 }}>
              {feedbackSummary}
            </div>
          </div>
        )}

        {/* Analysis divider */}
        {hasAnalysis && (allCriteria.length > 0 || strengths.length > 0 || improvements.length > 0) && (
          <div style={S.analysisDivider}>
            <div style={S.analysisDividerLine} />
            <span style={S.analysisDividerLabel}>Analysis</span>
            <div style={S.analysisDividerLine} />
          </div>
        )}

        {/* Criteria feedback grid */}
        {allCriteria.length > 0 && (
          <div style={allCriteria.length === 1 ? { marginBottom: '8px' } : S.criteriaGrid}>
            {allCriteria.map(({ key, text }) => {
              const theme = CRITERION_THEMES[key] ?? 'default';
              return (
                <div key={key} style={S.criterionBox(theme)}>
                  <span style={S.criterionLabel}>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                  {text}
                </div>
              );
            })}
          </div>
        )}

        {/* Strengths / Improvements */}
        {(strengths.length > 0 || improvements.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: strengths.length > 0 && improvements.length > 0 ? '1fr 1fr' : '1fr', gap: '6px', marginBottom: '6px' }}>
            {strengths.length > 0 && (
              <div style={S.listBox('green')}>
                <span style={S.listLabel}>Strengths</span>
                {strengths.map((s, i) => <div key={i}>• {s}</div>)}
              </div>
            )}
            {improvements.length > 0 && (
              <div style={S.listBox('amber')}>
                <span style={S.listLabel}>Areas for Improvement</span>
                {improvements.map((s, i) => <div key={i}>• {s}</div>)}
              </div>
            )}
          </div>
        )}

        {/* Diagnostic feedback */}
        {Array.isArray(answer.diagnostic_feedback) && answer.diagnostic_feedback.length > 0 && (
          <div style={{ marginTop: '6px' }}>
            <span style={{ ...S.contextLabel, color: '#be185d' }}>Why This Was Tempting</span>
            {answer.diagnostic_feedback.map((fb) => (
              <div key={fb.tag} style={{ ...S.contextBlock, background: '#fdf2f8', border: '1px solid #f9a8d4', color: '#831843', marginBottom: '4px' }}>
                <div>{fb.explain}</div>
                {fb.resource?.title && (
                  <div style={{ marginTop: '3px', fontSize: '10px', opacity: 0.8 }}>
                    Recommended: <strong>{fb.resource.title}</strong>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ModuleSection({
  module: moduleName,
  answers,
  moduleResult,
}: {
  module: string;
  answers: ModuleAnswer[];
  moduleResult: ModuleResult | undefined;
}) {
  const sectionGroups = groupBySection(answers);
  const sortedSections = Array.from(sectionGroups.entries()).sort(([a], [b]) => a - b);
  const scoreText = moduleResult
    ? `Score: ${moduleResult.score.toFixed(1)}%`
    : '';

  return (
    <div style={S.moduleWrapper}>
      <div style={S.moduleHeader}>
        <span style={S.moduleTitle}>{MODULE_LABELS[moduleName] ?? moduleName}</span>
        {scoreText && <span style={S.moduleScore}>{scoreText}</span>}
      </div>
      <div style={S.moduleContentBorder}>
        {sortedSections.map(([sectionNum, sectionAnswers]) => (
          <div key={sectionNum}>
            {sortedSections.length > 1 && (
              <div style={S.sectionHeader}>Section {sectionNum}</div>
            )}
            {sortAnswers(sectionAnswers).map((answer, idx) => (
              <QuestionCard key={answer.id} answer={answer} index={idx} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ModulePrintView({
  submission,
  modules,
  answersMap,
  sessionModules,
  onPrintDone,
}: Readonly<ModulePrintViewProps>) {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.print();
      onPrintDone();
    }, 250);
    return () => clearTimeout(timer);
  }, [onPrintDone]);

  const content = (
    <div id="submission-pdf-export">
      <div style={S.page}>
        {/* Document header */}
        <div style={S.docHeader}>
          <h1 style={S.docTitle}>{submission.username} — Exam Review</h1>
          <div style={S.docMeta}>
            <span style={S.docMetaItem}>
              <span style={S.docMetaLabel}>Exam:</span>
              {submission.exam_type.toUpperCase()}
            </span>
            {submission.level?.length > 0 && (
              <span style={S.docMetaItem}>
                <span style={S.docMetaLabel}>Level:</span>
                {submission.level.join(', ')}
              </span>
            )}
            <span style={S.docMetaItem}>
              <span style={S.docMetaLabel}>Date:</span>
              {new Date(submission.created_at).toLocaleDateString()}
            </span>
            <span style={S.docMetaItem}>
              <span style={S.docMetaLabel}>Email:</span>
              {submission.email}
            </span>
            <span style={S.docMetaItem}>
              <span style={S.docMetaLabel}>Session:</span>
              {submission.session_id.slice(0, 8)}…
            </span>
          </div>
        </div>

        {/* Modules */}
        {modules.map((mod) => (
          <ModuleSection
            key={mod}
            module={mod}
            answers={answersMap[mod] ?? []}
            moduleResult={sessionModules.find((m) => m.module === mod)}
          />
        ))}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
